import React, { useState, useEffect } from "react";

const LoanVsLeaseCalculator = () => {
  const [vehiclePrice, setVehiclePrice] = useState(2000000);
  const [downPayment, setDownPayment] = useState(400000);
  const [loanInterestRate, setLoanInterestRate] = useState(8.5);
  const [loanTenure, setLoanTenure] = useState(5);
  const [tenureType, setTenureType] = useState("years");
  const [leaseMonthlyPayment, setLeaseMonthlyPayment] = useState(35000);
  const [result, setResult] = useState({
    loanEMI: "0.00",
    loanTotalPayment: "0.00",
    loanTotalInterest: "0.00",
    leaseTotalPayment: "0.00",
    costDifference: "0.00",
    betterOption: "Loan",
    monthlySavings: "0.00",
    ownershipValue: "0.00",
  });

  // Loan vs Lease Calculation Function
  const calculateLoanVsLease = () => {
    const price = parseFloat(vehiclePrice);
    const down = parseFloat(downPayment);
    const rate = parseFloat(loanInterestRate) / 100 / 12;
    let tenure = parseFloat(loanTenure);
    const leasePayment = parseFloat(leaseMonthlyPayment);

    if (isNaN(price) || isNaN(down) || isNaN(rate) || isNaN(tenure) || price <= 0 || tenure <= 0) return;

    const months = tenureType === "years" ? tenure * 12 : tenure;
    const loanAmount = price - down;

    // Calculate Loan EMI
    let emi;
    if (rate > 0) {
      emi = (loanAmount * rate * Math.pow(1 + rate, months)) / (Math.pow(1 + rate, months) - 1);
    } else {
      emi = loanAmount / months;
    }

    // Loan calculations
    const loanTotalPayment = (emi * months) + down;
    const loanTotalInterest = (emi * months) - loanAmount;

    // Lease calculations
    const leaseTotalPayment = leasePayment * months;

    // Comparison
    const costDifference = Math.abs(loanTotalPayment - leaseTotalPayment);
    const betterOption = loanTotalPayment < leaseTotalPayment ? "Loan" : "Lease";
    const monthlySavings = Math.abs(emi - leasePayment);
    const ownershipValue = price * 0.6; // Assuming 60% residual value

    setResult({
      loanEMI: emi.toFixed(2),
      loanTotalPayment: loanTotalPayment.toFixed(2),
      loanTotalInterest: loanTotalInterest.toFixed(2),
      leaseTotalPayment: leaseTotalPayment.toFixed(2),
      costDifference: costDifference.toFixed(2),
      betterOption: betterOption,
      monthlySavings: monthlySavings.toFixed(2),
      ownershipValue: ownershipValue.toFixed(2),
    });
  };

  useEffect(() => {
    calculateLoanVsLease();
  }, []);

  const tenureLabel = tenureType === "years" ? "Years" : "Months";

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <path d="M14 16c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2z"></path>
              <path d="M19 16c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2z"></path>
              <path d="M5 16c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2z"></path>
              <path d="M8.5 14.5 3 9l3-3 5.5 5.5z"></path>
              <path d="M11.5 3.5 16 8l-3 3-5.5-5.5z"></path>
            </svg>
          </div>
          <h1>Loan vs Lease Calculator</h1>
          <p>
            Compare the costs of buying with a loan versus leasing to make the best financial decision
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                  <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                  <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                </svg>
                Vehicle & Financing Details
              </h2>

              {/* Vehicle Price */}
              <div className="input-group">
                <label>Vehicle Price</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={vehiclePrice}
                    onChange={(e) => setVehiclePrice(e.target.value)}
                    className="calc-input"
                    placeholder="2000000"
                  />
                </div>
                <div className="quick-select-group">
                  {[500000, 1000000, 2000000, 5000000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setVehiclePrice(amount)}
                      className="quick-select-btn primary"
                    >
                      ₹{(amount / 100000).toFixed(0)}L
                    </button>
                  ))}
                </div>
              </div>

              {/* Down Payment */}
              <div className="input-group">
                <label>Down Payment (for Loan)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={downPayment}
                    onChange={(e) => setDownPayment(e.target.value)}
                    className="calc-input"
                    placeholder="400000"
                  />
                </div>
                <div className="quick-select-group">
                  {[100000, 200000, 400000, 500000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setDownPayment(amount)}
                      className="quick-select-btn secondary"
                    >
                      ₹{(amount / 100000).toFixed(0)}L
                    </button>
                  ))}
                </div>
              </div>

              {/* Loan Interest Rate */}
              <div className="input-group">
                <label>Loan Interest Rate (Annual)</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={loanInterestRate}
                    onChange={(e) => setLoanInterestRate(e.target.value)}
                    className="calc-input"
                    placeholder="8.5"
                    step="0.1"
                  />
                  <span className="input-suffix">%</span>
                </div>
                <div className="quick-select-group">
                  {[7, 8.5, 10, 12].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setLoanInterestRate(rate)}
                      className="quick-select-btn success"
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Loan Tenure */}
              <div className="input-group">
                <label>Loan/Lease Tenure</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={loanTenure}
                    onChange={(e) => setLoanTenure(e.target.value)}
                    className="calc-input"
                    placeholder="5"
                  />
                </div>
                <div className="quick-select-group">
                  {tenureType === "years" 
                    ? [3, 5, 7, 10].map((years) => (
                        <button
                          key={years}
                          onClick={() => setLoanTenure(years)}
                          className="quick-select-btn primary"
                        >
                          {years}Y
                        </button>
                      ))
                    : [36, 60, 84, 120].map((months) => (
                        <button
                          key={months}
                          onClick={() => setLoanTenure(months)}
                          className="quick-select-btn primary"
                        >
                          {months}M
                        </button>
                      ))
                  }
                </div>
              </div>

              {/* Tenure Type */}
              <div className="input-group">
                <label>Tenure Type</label>
                <div className="quick-select-group">
                  {[
                    { value: "years", label: "Years" },
                    { value: "months", label: "Months" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => {
                        setTenureType(type.value);
                        setLoanTenure(type.value === "years" ? 5 : 60);
                      }}
                      className={`quick-select-btn ${tenureType === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Lease Monthly Payment */}
              <div className="input-group">
                <label>Lease Monthly Payment</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={leaseMonthlyPayment}
                    onChange={(e) => setLeaseMonthlyPayment(e.target.value)}
                    className="calc-input"
                    placeholder="35000"
                  />
                </div>
                <div className="quick-select-group">
                  {[20000, 30000, 40000, 50000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setLeaseMonthlyPayment(amount)}
                      className="quick-select-btn secondary"
                    >
                      ₹{(amount / 1000).toFixed(0)}K
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculateLoanVsLease} className="calculate-btn">
                Compare Options
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                Comparison Results
              </h2>

              <div className="result-card">
                {/* Better Option - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Better Option</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="m9 12 2 2 4-4"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.betterOption}
                  </div>
                  <div className="result-subtext">
                    Save ₹{Number(result.costDifference).toLocaleString("en-IN")} total
                  </div>
                </div>

                {/* Loan Total Cost */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Loan Total Cost</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                        <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                        <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.loanTotalPayment).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    EMI: ₹{Number(result.loanEMI).toLocaleString("en-IN")}/month
                  </div>
                </div>

                {/* Lease Total Cost */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Lease Total Cost</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                        <polyline points="16 7 22 7 22 13"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.leaseTotalPayment).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Monthly: ₹{Number(leaseMonthlyPayment).toLocaleString("en-IN")}
                  </div>
                </div>

                {/* Ownership Value */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Ownership Value (Loan)</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.ownershipValue).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Estimated residual asset value
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Cost Comparison</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Loan Total Cost</span>
                      <span className="breakdown-percentage">
                        ₹{Number(result.loanTotalPayment).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${(parseFloat(result.loanTotalPayment) / Math.max(parseFloat(result.loanTotalPayment), parseFloat(result.leaseTotalPayment))) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Lease Total Cost</span>
                      <span className="breakdown-percentage">
                        ₹{Number(result.leaseTotalPayment).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${(parseFloat(result.leaseTotalPayment) / Math.max(parseFloat(result.loanTotalPayment), parseFloat(result.leaseTotalPayment))) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding Loan vs Lease Decisions</h3>
              <p>
                Choosing between buying a vehicle with a loan or leasing it is a significant financial decision that 
                depends on your personal circumstances, driving habits, and financial goals. When you buy with a loan, 
                you make a down payment and monthly payments until you own the vehicle outright. This option builds 
                equity and gives you full ownership, but requires higher monthly payments and you're responsible for 
                all maintenance after warranty. Leasing typically offers lower monthly payments and you can drive a new 
                vehicle every few years, but you never own the vehicle and must stay within mileage limits. This 
                calculator helps you compare the total costs of both options, factoring in loan interest, down payment, 
                and the residual value of ownership to help you make an informed decision.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Calculation Formulas:</h4>
            <div className="formula-display">
              Loan EMI = [P × r × (1+r)^n] / [(1+r)^n - 1]
            </div>
            <div className="formula-display">
              Total Loan Cost = (EMI × Months) + Down Payment
            </div>
            <div className="formula-display">
              Total Lease Cost = Monthly Lease Payment × Months
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">P</div>
                <div className="formula-param-value">Principal (Vehicle Price - Down Payment)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">r</div>
                <div className="formula-param-value">Monthly interest rate</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">n</div>
                <div className="formula-param-value">Number of months</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Ownership Value</div>
                <div className="formula-param-value">Estimated residual value (typically 50-70%)</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>What is the main difference between buying and leasing a vehicle?</h5>
              <p>
                When you buy with a loan, you own the vehicle after paying it off and build equity. Leasing is like renting - you make lower monthly payments but never own the vehicle and must return it at lease end. Buying offers long-term value and no restrictions, while leasing provides lower payments and the ability to drive new cars frequently.
              </p>
            </div>

            <div className="faq-item">
              <h5>Which is more cost-effective: buying or leasing?</h5>
              <p>
                Buying is typically more cost-effective long-term because you build equity and own an asset. However, leasing can be cheaper short-term with lower monthly payments and no maintenance worries during warranty. If you keep vehicles for 7+ years and drive high mileage, buying is usually better. If you prefer new cars every 3-4 years and drive moderate miles, leasing may suit you.
              </p>
            </div>

            <div className="faq-item">
              <h5>What are the advantages of leasing a vehicle?</h5>
              <p>
                Leasing advantages include lower monthly payments (typically 30-50% less than loan payments), ability to drive a new car every few years, warranty coverage for most repairs, no trade-in hassles, and potential tax benefits for business use. You also avoid depreciation risk and can access higher-end vehicles for the same monthly budget.
              </p>
            </div>

            <div className="faq-item">
              <h5>What are the disadvantages of leasing?</h5>
              <p>
                Leasing disadvantages include no ownership equity, mileage restrictions (typically 10,000-15,000 km/year), penalties for excess wear and tear, continuous monthly payments without building value, and early termination fees. You can't modify the vehicle and must maintain comprehensive insurance. Over time, perpetual leasing costs more than buying.
              </p>
            </div>

            <div className="faq-item">
              <h5>What factors should I consider when deciding between loan and lease?</h5>
              <p>
                Consider your annual driving distance (high mileage favors buying), how long you keep vehicles (longer ownership favors buying), upfront cash availability (leasing needs less), monthly budget (leasing offers lower payments), vehicle condition habits (rough use favors buying), and desire for latest technology (leasing offers newer models). Also consider tax implications and business vs personal use.
              </p>
            </div>

            <div className="faq-item">
              <h5>Can I buy the vehicle at the end of a lease?</h5>
              <p>
                Yes, most leases include a purchase option at lease end for a predetermined residual value (typically 50-60% of original price). This can be beneficial if the vehicle's market value exceeds the buyout price or if you've grown attached to it. You can finance the buyout with a new loan or pay cash. Compare the buyout price to similar used vehicles before deciding.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoanVsLeaseCalculator;